import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getSubscriptionsByCategory } from '../api/subscriptions'
import { useSubscriptions } from '../hooks/useSubscriptions'
import { useRequireAuth } from '../hooks/useRequireAuth'
import { useCheckoutBundleStore } from '../store/checkoutBundleStore'
import { trackViewContent, trackAddToCart } from '../utils/metaPixel'
import SubscriptionDetailTemplate from '../components/SubscriptionDetailTemplate'
import VerticalGenreCarousel from '../components/VerticalGenreCarousel'

/**
 * Thematic subscriptions page.
 *
 * Loads thematic genres from the backend API.
 * Desktop: vertical genre carousel on the left, shared detail template on the right.
 * Mobile: horizontal swipeable carousel on top, detail template below.
 * URL query param `?genre=<id>` tracks the selected genre.
 */
export default function ThematicSubscriptionsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const requireAuth = useRequireAuth()
  const fetchThematic = useCallback(() => getSubscriptionsByCategory('thematic'), [])
  const { subscriptions: thematic, loading, error } = useSubscriptions(fetchThematic)
  const { addTier, items, isMaxReached } = useCheckoutBundleStore()

  const genreId = searchParams.get('genre')
  const activeIndex = useMemo(() => {
    const idx = thematic.findIndex((s) => s.id === genreId)
    return idx >= 0 ? idx : 0
  }, [thematic, genreId])

  const activeSub = thematic[activeIndex]

  useEffect(() => {
    if (!activeSub) return
    if (genreId !== activeSub.id) {
      setSearchParams({ genre: activeSub.id }, { replace: true })
    }
    trackViewContent(activeSub)
  }, [activeSub, genreId, setSearchParams])

  const handleSelect = (index) => {
    const next = thematic[index]
    if (next) {
      setSearchParams({ genre: next.id }, { replace: true })
    }
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 pb-12 text-center">
        <p className="text-brand-blue">{t('common.loading')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 pb-12 text-center">
        <p className="text-red-600">{error}</p>
      </div>
    )
  }

  if (thematic.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 pb-12 text-center min-h-[calc(100vh-10rem)] flex flex-col justify-center">
        <h1 className="font-climate text-4xl md:text-6xl text-brand-blue mb-6">
          {t('nav.thematicSubscriptions')}
        </h1>
        <p className="text-brand-blue/80">{t('thematic.emptyCatalog', 'Wkrótce pojawią się nowe kategorie tematyczne.')}</p>
      </div>
    )
  }

  const bundleItem = items.find((item) => item.slug === activeSub?.id)
  const inBundle = Boolean(bundleItem)
  const bundleQuantity = bundleItem?.quantity || 0
  const maxReached = isMaxReached()

  const handleChoose = () => {
    const returnUrl = `${window.location.origin}/checkout`
    const authUrl = requireAuth(returnUrl)
    if (authUrl !== returnUrl) {
      window.location.href = authUrl
      return
    }
    addTier(activeSub, { genres: [activeSub.id] })
    trackAddToCart(activeSub)
    navigate('/checkout')
  }

  const handleAddToBundle = () => {
    addTier(activeSub, { genres: [activeSub.id] })
    trackAddToCart(activeSub)
  }

  const handleGoToCheckout = () => {
    navigate('/checkout')
  }

  return (
    <div className="max-w-6xl mx-auto px-4 pb-12 min-h-[calc(100vh-10rem)] flex flex-col">
      <div className="text-center mb-8 md:mb-12">
        <h1 className="font-climate text-4xl md:text-6xl text-brand-blue">
          {t('nav.thematicSubscriptions')}
        </h1>
      </div>

      <div className="flex-1 flex items-start justify-center pt-4 md:pt-[7vh]">
        <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center w-full">
          <VerticalGenreCarousel
            items={thematic}
            activeIndex={activeIndex}
            onSelect={handleSelect}
            ariaLabel={t('nav.thematicSubscriptions')}
          />

          <SubscriptionDetailTemplate
            subscription={activeSub}
            mode="recurring"
            showPoster={false}
            inBundle={inBundle}
            bundleQuantity={bundleQuantity}
            maxReached={maxReached}
            onChoose={handleChoose}
            onAddToBundle={handleAddToBundle}
            onGoToCheckout={handleGoToCheckout}
          />
        </div>
      </div>
    </div>
  )
}
